import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { callRpc } from '@/lib/supabaseTyped';
import { logger } from '@/lib/logger';

/**
 * Hook that checks if the current user is a sysadmin.
 *
 * Dual-layer check:
 *   1. JWT `app_metadata.role === 'admin'` (fastest, no RPC roundtrip)
 *   2. `has_sysadmin()` RPC fallback (hits user_roles table)
 *
 * IMPORTANT: This hook is mounted in many components simultaneously
 * (TopBar, ProfileMenu, MobileTopBar, AiAssistantDialog, Admin). To avoid
 * a "thundering herd" of `has_sysadmin` RPC calls — and historically a
 * `supabase.auth.refreshSession()` storm that revoked refresh tokens and
 * silently signed non-admin users out — we cache the per-user probe
 * promise at module scope. All concurrent mounts for the same user share
 * one inflight RPC and one resolved result for the lifetime of the tab.
 *
 * If an operator grants admin via SQL while the browser is open, the
 * change will surface on the next session refresh / sign-out+sign-in
 * (or via `clearAdminCheckCache()` exposed below). We deliberately do
 * NOT auto-refresh the session inside this hook — that was the source
 * of the parallel-refresh logout bug.
 */

const cache = new Map<string, Promise<boolean>>();

export function clearAdminCheckCache(userId?: string): void {
  if (userId) cache.delete(userId);
  else cache.clear();
}

async function probeIsAdmin(userId: string, appMetadata: unknown): Promise<boolean> {
  const meta = appMetadata as Record<string, unknown> | undefined;
  if (meta?.role === 'admin' || meta?.isAdmin === true) {
    logger.info('[useAdminAuth] admin via app_metadata');
    return true;
  }
  try {
    const { data, error } = await callRpc<boolean>('has_sysadmin', { _user_id: userId });
    logger.info('[useAdminAuth] has_sysadmin RPC', {
      userId, data, error: error?.message ?? null,
    });
    return data === true && !error;
  } catch (e) {
    logger.error('[useAdminAuth] RPC threw', {
      userId, message: e instanceof Error ? e.message : String(e),
    });
    return false;
  }
}

function getOrCreateProbe(userId: string, appMetadata: unknown): Promise<boolean> {
  let p = cache.get(userId);
  if (!p) {
    p = probeIsAdmin(userId, appMetadata);
    cache.set(userId, p);
  }
  return p;
}

export function useAdminAuth() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !user) {
      setIsAdmin(false);
      setIsChecking(false);
      return;
    }

    let cancelled = false;
    setIsChecking(true);

    getOrCreateProbe(user.id, user.app_metadata).then((result) => {
      if (cancelled) return;
      setIsAdmin(result);
      setIsChecking(false);
    });

    return () => { cancelled = true; };
  }, [user, isAuthenticated, authLoading]);

  return {
    isAdmin,
    isChecking: authLoading || isChecking,
    user,
    isAuthenticated,
  };
}
